/**
 * Vanity subdomains (stackranking, resumeflow, jdcraft) share the same SPA but auth
 * cookies are host-scoped. Redirect to the canonical ReachOut host so SSO runs once.
 *
 * Configure with REACT_APP_CANONICAL_HOST (baked at build time).
 * If unset, infer from the current host (demo / uat / prod) — never send demo → prod.
 */

const DEFAULT_CANONICAL_HOST = 'reachout.hirecrux.ai';

/** @type {Record<string, string>} hostname -> default path when user opens `/` only */
const VANITY_ROOT_PATHS = {
  // Prod
  'stackranking.hirecrux.ai': '/existing-positions',
  'resumeflow.hirecrux.ai': '/resume-formatter',
  'jdcraft.hirecrux.ai': '/open-positions',
  // Demo
  'stackranking.demo.hirecrux.ai': '/existing-positions',
  'resumeflow.demo.hirecrux.ai': '/resume-formatter',
  'jdcraft.demo.hirecrux.ai': '/open-positions',
  // UAT
  'stackranking.uat.hirecrux.ai': '/existing-positions',
  'resumeflow.uat.hirecrux.ai': '/resume-formatter',
  'jdcraft.uat.hirecrux.ai': '/open-positions',
};

function inferCanonicalHost(hostname) {
  const host = (hostname || '').toLowerCase();
  if (host.endsWith('.demo.hirecrux.ai') || host === 'demo.hirecrux.ai') {
    return 'reachout.demo.hirecrux.ai';
  }
  if (host.endsWith('.uat.hirecrux.ai') || host === 'uat.hirecrux.ai') {
    return 'reachout.uat.hirecrux.ai';
  }
  return DEFAULT_CANONICAL_HOST;
}

function getCanonicalHost(currentHostname) {
  const fromEnv = (process.env.REACT_APP_CANONICAL_HOST ?? '').trim().toLowerCase();
  if (fromEnv) {
    return fromEnv;
  }
  return inferCanonicalHost(currentHostname);
}

/**
 * @returns {boolean} true if a full-page redirect was started (caller should not mount React)
 */
export function maybeRedirectToCanonicalHost() {
  if (typeof window === 'undefined') {
    return false;
  }

  const host = window.location.hostname.toLowerCase();
  const canonicalHost = getCanonicalHost(host);

  if (host === canonicalHost) {
    return false;
  }

  const defaultRootPath = VANITY_ROOT_PATHS[host];
  if (!defaultRootPath) {
    return false;
  }

  const { pathname, search, hash, protocol } = window.location;
  const isRoot = pathname === '/' || pathname === '';
  const targetPath = isRoot ? defaultRootPath : pathname;

  const targetUrl = `${protocol}//${canonicalHost}${targetPath}${search}${hash}`;
  window.location.replace(targetUrl);
  return true;
}
